import { Component, OnDestroy, signal } from '@angular/core';
import { NavigationCancel, NavigationEnd, NavigationError, NavigationSkipped, NavigationStart, Router, RouterOutlet } from '@angular/router';
import { FeedbackCenterComponent } from './components/ui/feedback-center.component';
import { AdminTopbarComponent } from './components/ui/admin-topbar.component';
import { AdminNavigationComponent } from './components/ui/admin-navigation.component';
import { AuthService } from './services/auth.service';
import { RouteLoadingService } from './services/route-loading.service';
import { Subscription } from 'rxjs';
import { CommonModule } from '@angular/common';
import { initCharLimitHints } from './shared/char-limit-hints';
import { initInputGuards } from './shared/input-guard';
import { initScrollHoverGuard } from './shared/scroll-hover-guard';
import { RouteSkeletonComponent } from './components/ui/route-skeleton.component';
import { SuscripcionComponent } from './components/suscripcion/suscripcion.component';
import { AccessBlockedComponent } from './components/ui/access-blocked.component';
import { KuboDockComponent } from './components/ui/kubo-dock.component';
import { ThemeService } from './services/theme.service';
import { SceneTransition } from './services/scene-transition.service';
import { initSelectEnhancer } from './shared/select-enhancer';

@Component({
  selector: 'app-root',
  imports: [CommonModule, RouterOutlet, FeedbackCenterComponent, AdminTopbarComponent, AdminNavigationComponent, RouteSkeletonComponent, SuscripcionComponent, AccessBlockedComponent, KuboDockComponent],
  templateUrl: './app.html',
  styleUrl: './app.css'
})
export class App implements OnDestroy {
  private readonly routerSubscription: Subscription;
  private readonly currentUrl = signal('');
constructor(public auth: AuthService, router: Router, public routeLoading: RouteLoadingService, private readonly theme: ThemeService, private readonly scene: SceneTransition) {
    initCharLimitHints();
    initInputGuards();
    initScrollHoverGuard();
    initSelectEnhancer();
    this.routerSubscription = router.events.subscribe(event => {
      // El cambio de piel y la visibilidad del shell se retrasan hasta que el
      // velo cubra (cruce acceso <-> panel): ver SceneTransition. Mientras
      // tanto la vista saliente se queda intacta y sin desplazarse.
      if (event instanceof NavigationStart) {
        if (!this.scene.willCurtain(router.url, event.url)) this.currentUrl.set(event.url);
        this.routeLoading.beginNavigation(event.url);
      }
      if (event instanceof NavigationEnd) { this.currentUrl.set(event.urlAfterRedirects); this.theme.applyForRoute(event.urlAfterRedirects.startsWith('/login')); }
      // NavigationSkipped: Angular Router la dispara (en vez de NavigationEnd)
      // cuando decide que la URL de destino es igual a la actual y omite la
      // navegación (onSameUrlNavigation:'ignore', el valor por defecto — no
      // hay config explícita en app.config.ts). Puede pasar con una
      // navegación que solo cambia el fragmento si window.location.hash ya
      // se había fijado a mano un instante antes (p. ej. el botón "Ir" de
      // Kubo). Sin escuchar este evento, la pantalla de carga global se
      // queda encendida indefinidamente (hasta el failsafe de 12s) aunque
      // la navegación en sí no haya fallado, solo se consideró innecesaria.
      if (event instanceof NavigationEnd || event instanceof NavigationCancel || event instanceof NavigationError || event instanceof NavigationSkipped) {
        this.routeLoading.finishNavigation();
        this.scene.markSettled();
        // Fin de la navegación: el velo (si lo hubo) se disuelve sobre la
        // vista que acaba de montarse. También en fallo/cancelación, para
        // que no se quede la pantalla tapada.
        this.scene.curtainOut();
      }
    });
    this.currentUrl.set(router.url);
    this.theme.applyForRoute(router.url.startsWith('/login'));
    if (this.showAdminShell()) this.routeLoading.showInitialLoad(router.url);
  }
  ngOnDestroy(): void { this.routerSubscription.unsubscribe(); }
  showAdminShell(): boolean { return !!this.auth.currentUser() && !this.currentUrl().startsWith('/login'); }
  // Solo el propietario (legal o delegado) puede renovar/contratar el plan.
  isOwner(): boolean { return this.auth.currentUser()?.role === 'owner'; }
  // Lleva al selector de planes dentro de la pantalla de suscripción del
  // aviso de acceso pausado (prueba vencida o plan restringido).
  scrollToPlanChoice(): void {
    document.querySelector('.ab-panel .trial-actions, .ab-panel .plan-selector')
      ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }
  skeletonVariant(): 'cards'|'list'|'table'|'form'|'detail' {
    const url = this.routeLoading.url();
    if (/module=(reservas|usuarios|superadmin)/.test(url)) return 'table';
    if (/module=(configuracion|ingresosNegocio|subadministrador)/.test(url)) return 'form';
    if (/module=(novedades|superadminNovedades|reciclaje)/.test(url)) return 'list';
    return 'cards';
  }
  protected readonly title = signal('admin-hubsv');
}
